import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ShoppingBagIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import PageHeader from '../ui/PageHeader';
import Button from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  ELEVATE_HISPANO_SLUG,
  ELEVATE_HISPANO_EVENT_SRC,
  ELEVATE_WELCOME_DISCOUNT_PERCENT,
  BANNERS_NEW_HOME_SRC,
  EVOLUCION_90_REGISTER_URL,
  buildElevateBuyUrl,
} from '../../utils/elevateEvent';

function ElevateHispanoTicketCard({ copy, buyUrl, welcomeDiscount, giftOnTheWayCopy }) {
  const hasDiscount = Boolean(welcomeDiscount?.available);
  const purchased = Boolean(welcomeDiscount?.claimed);
  const percent = welcomeDiscount?.percent || ELEVATE_WELCOME_DISCOUNT_PERCENT;

  return (
    <motion.article
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1] }}
      className="card-premium overflow-hidden h-full"
    >
      <div className="flex h-full flex-col">
        <div className="relative flex aspect-[16/10] items-center justify-center overflow-hidden bg-white px-8 py-10">
          <img
            src={ELEVATE_HISPANO_EVENT_SRC}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full scale-105 object-cover blur-[2px]"
          />
          <div className="absolute inset-0 bg-white/25" />
          {hasDiscount ? (
            <span className="absolute right-3 top-3 z-20 inline-flex rounded-full bg-orange-500 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white shadow-sm">
              {copy.welcomeDiscountBadge?.replace('{percent}', String(percent))
                || `${percent}% OFF welcome gift`}
            </span>
          ) : null}
          <div className="relative z-10 rounded-xl bg-white/70 px-5 py-4 text-center shadow-sm backdrop-blur-[1px]">
            <p className="text-2xs font-semibold uppercase tracking-[0.22em] text-amber-700">
              {copy.elevateEyebrow || 'Event'}
            </p>
            <p className="mt-3 text-2xl font-bold tracking-tight text-secondary-900 sm:text-3xl">
              {copy.elevateTitle || 'Elevate Hispanos'}
            </p>
            <p className="mt-2 text-sm font-semibold text-secondary-800">
              {copy.elevateEventLine || 'Las Vegas · November 14, 2026'}
            </p>
          </div>
        </div>
        <div className="flex flex-1 flex-col p-5 sm:p-6">
          <p className="text-2xs font-semibold uppercase tracking-[0.18em] text-amber-600 ">
            {copy.elevateEyebrow || 'Event'}
          </p>
          <h3 className="mt-1 text-xl font-bold text-secondary-900 ">
            {copy.elevateTitle || 'Elevate Hispanos'}
          </h3>
          <p className="mt-1 text-sm font-medium text-orange-600 ">
            {copy.elevateEventLine || 'Las Vegas · November 14, 2026'}
          </p>
          <p className="mt-3 text-sm leading-relaxed text-secondary-600 ">
            {purchased
              ? (giftOnTheWayCopy || 'Your gift is on the way')
              : hasDiscount
                ? (copy.welcomeDiscountDescription?.replace('{percent}', String(percent))
                  || `Your welcome gift is active: ${percent}% off this event ticket.`)
                : (copy.elevateDescription
                  || 'Purchase your pre-sale ticket now and secure your seat.')}
          </p>
          <Button
            href={purchased ? undefined : buyUrl}
            target={purchased ? undefined : '_blank'}
            rel="noopener noreferrer"
            variant={purchased ? 'secondary' : 'primary'}
            size="compact"
            disabled={purchased}
            className="mt-5 w-full min-h-[44px] justify-center"
            icon={ArrowTopRightOnSquareIcon}
            iconPosition="right"
          >
            {purchased
              ? (giftOnTheWayCopy || 'Your gift is on the way')
              : hasDiscount
                ? (copy.welcomeDiscountBuyCta?.replace('{percent}', String(percent))
                  || `Buy with ${percent}% off`)
                : (copy.elevateBuyCta || 'Buy ticket')}
          </Button>
        </div>
      </div>
    </motion.article>
  );
}

function Evolucion90Card({ copy }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.08, ease: [0.23, 1, 0.32, 1] }}
      className="card-premium overflow-hidden h-full"
    >
      <div className="flex h-full flex-col">
        <div className="relative flex aspect-[16/10] items-center justify-center overflow-hidden bg-white px-8 py-10">
          <img
            src={BANNERS_NEW_HOME_SRC}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full scale-105 object-cover blur-[2px]"
          />
          <div className="absolute inset-0 bg-white/25" />
          <div className="relative z-10 rounded-xl bg-white/70 px-5 py-4 text-center shadow-sm backdrop-blur-[1px]">
            <p className="text-2xs font-semibold uppercase tracking-[0.22em] text-amber-700">
              {copy.evolucion90Brand || 'Elantar Academy'}
            </p>
            <p className="mt-3 text-2xl font-bold tracking-tight text-secondary-900 sm:text-3xl">
              {copy.evolucion90OverlayTitle || 'EVOLUCIÓN 90™'}
            </p>
            <p className="mt-2 text-sm font-semibold text-secondary-800">
              {copy.evolucion90WeekLabel || 'Week 02'}
            </p>
          </div>
        </div>
        <div className="flex flex-1 flex-col p-5 sm:p-6">
          <p className="text-2xs font-semibold uppercase tracking-[0.18em] text-amber-600 ">
            {copy.evolucion90Eyebrow || 'EVOLUCIÓN 90™ · Week 02'}
          </p>
          <h3 className="mt-1 text-xl font-bold text-secondary-900 ">
            {copy.evolucion90Title || 'From Producer to Leader'}
          </h3>
          <p className="mt-1 text-sm font-medium text-orange-600 ">
            {copy.evolucion90EventLine ||
              'Thursday, August 27, 2026 · 5:00–6:30 PM (PT)'}
          </p>
          <p className="mt-3 text-sm leading-relaxed text-secondary-600 ">
            {copy.evolucion90Description ||
              'Make the leap from producer to leader. Live Zoom session with Freddy Reynoso.'}
          </p>
          <Button
            href={EVOLUCION_90_REGISTER_URL}
            target="_blank"
            rel="noopener noreferrer"
            variant="primary"
            size="compact"
            className="mt-5 w-full min-h-[44px] justify-center"
            icon={ArrowTopRightOnSquareIcon}
            iconPosition="right"
          >
            {copy.evolucion90Cta || 'Attend Evolucion 90'}
          </Button>
        </div>
      </div>
    </motion.article>
  );
}

function ClientClubView() {
  const { user, setUser } = useAuth();
  const { t } = useLanguage();
  const e = t.enrola;
  const c = e.clubView || {};
  const isElevateHispano = user?.company_slug === ELEVATE_HISPANO_SLUG;
  const [welcomeDiscount, setWelcomeDiscount] = useState(null);
  const [eventStatus, setEventStatus] = useState(null);

  useEffect(() => {
    if (!isElevateHispano) {
      return undefined;
    }
    let cancelled = false;
    axios
      .post('/api/client/elevate-event/check-purchase', {}, { withCredentials: true })
      .then((checkResponse) => {
        const points = checkResponse?.data?.reward_points;
        if (typeof points === 'number') {
          setUser((prev) => (prev ? { ...prev, reward_points: points } : prev));
        }
        return axios.get('/api/client/elevate-event/status', { withCredentials: true });
      })
      .catch(() => null)
      .then((response) => {
        if (!cancelled && response) {
          setEventStatus(response.data || null);
          setWelcomeDiscount(response.data?.welcome_discount || null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEventStatus(null);
          setWelcomeDiscount(null);
        }
      });
    return () => {
      cancelled = true;
    };
    // user is intentionally not a dependency: re-running on every setUser
    // (reward_points sync) would re-trigger the purchase check in a loop.
  }, [isElevateHispano, setUser]);

  const buyUrl = useMemo(() => {
    const hasDiscount = Boolean(welcomeDiscount?.available);
    if (hasDiscount && eventStatus?.checkout_url) {
      return eventStatus.checkout_url;
    }
    return buildElevateBuyUrl(user, {
      discountPercent: hasDiscount
        ? (welcomeDiscount.percent || ELEVATE_WELCOME_DISCOUNT_PERCENT)
        : null,
      platformCode: eventStatus?.platform_code || 'enrola',
      points: hasDiscount
        ? (eventStatus?.discount_points || user?.reward_points || null)
        : null,
    });
  }, [user, welcomeDiscount, eventStatus]);

  if (!isElevateHispano) {
    return null;
  }

  return (
    <div className="space-y-6 xl:space-y-7">
      <PageHeader
        eyebrow="Enrola"
        icon={ShoppingBagIcon}
        title={e.club}
        subtitle={c.elevateSubtitle || c.subtitle}
      />

      <div className="flex flex-col gap-4 md:flex-row md:gap-6">
        <div className="min-w-0 flex-1">
          <ElevateHispanoTicketCard
            copy={c}
            buyUrl={buyUrl}
            welcomeDiscount={welcomeDiscount}
            giftOnTheWayCopy={t.dashboard?.giftOnTheWay}
          />
        </div>
        <div className="min-w-0 flex-1">
          <Evolucion90Card copy={c} />
        </div>
      </div>
    </div>
  );
}

export default ClientClubView;
